import { useQueries, useQuery } from "@tanstack/react-query";
import {
  fetchProduct,
  fetchProductOptions,
  fetchProductOptionList,
  fetchProductOptionItem,
} from "../../webService/webService";
import { queryKeys } from "../queryKeys";

// Products rarely change mid-session and the same product is shown
// simultaneously in cart, checkout and order history — a longer staleTime
// means those screens share one cached fetch instead of one each.
const PRODUCT_STALE_TIME = 60_000;

export const useProduct = (productId, options = {}) =>
  useQuery({
    queryKey: queryKeys.products.detail(productId),
    queryFn: () => fetchProduct(productId),
    enabled: Boolean(productId),
    staleTime: PRODUCT_STALE_TIME,
    ...options,
  });

export const useProductOptions = (productId, options = {}) =>
  useQuery({
    queryKey: queryKeys.products.options(productId),
    queryFn: () => fetchProductOptions(productId),
    enabled: Boolean(productId),
    staleTime: PRODUCT_STALE_TIME,
    ...options,
  });

export const useProductOptionList = (productId, optionId, options = {}) =>
  useQuery({
    queryKey: queryKeys.products.optionList(productId, optionId),
    queryFn: () => fetchProductOptionList(productId, optionId),
    enabled: Boolean(productId && optionId),
    staleTime: PRODUCT_STALE_TIME,
    ...options,
  });

export const useProductOptionItem = (productId, optionId, itemId, options = {}) =>
  useQuery({
    queryKey: queryKeys.products.optionItem(productId, optionId, itemId),
    queryFn: () => fetchProductOptionItem(productId, optionId, itemId),
    enabled: Boolean(productId && optionId && itemId),
    staleTime: PRODUCT_STALE_TIME,
    ...options,
  });

/**
 * Resolves a cart/order line's `selectedOptions` (each `{ optionId, optionItemId }`)
 * into their option-item records, one cached query per item — the same option
 * item selected in two different cart lines is fetched once. Mirrors the
 * `Promise.all(selectedOptions.map(fetchProductOptionItem))` pattern previously
 * duplicated in CartItem.jsx/OrderItem.jsx, but with per-item caching.
 */
export const useProductOptionItems = (productId, selectedOptions = [], options = {}) => {
  const valid = (selectedOptions || []).filter((s) => s?.optionId && s?.optionItemId);

  const results = useQueries({
    queries: valid.map((s) => ({
      queryKey: queryKeys.products.optionItem(productId, s.optionId, s.optionItemId),
      queryFn: () => fetchProductOptionItem(productId, s.optionId, s.optionItemId),
      enabled: Boolean(productId),
      staleTime: PRODUCT_STALE_TIME,
      ...options,
    })),
  });

  return {
    data: results.map((r) => r.data).filter((d) => d?.value),
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
  };
};
